import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import ConfirmIcon from 'assets/icons/confirm-icon'
import RejectIcon from 'assets/icons/reject-icon'
import RemoveIcon from 'assets/icons/remove-icon'
import ViewSiteIcon from 'assets/icons/view-site-icon'
import ActionsMenu from 'features/ActionMenu'
import { confirmSpecies, requestUpdateSiteSpecie } from 'api/siteSpecie'
import { SITE_STATUS_NOMINATED_ID, SITE_STATUS_DRAFT_ID } from 'constants/siteStatuses'
import { canConfirmSites, canNotViewUpdateSitesData } from 'utils/rolePermissions'
import { ProfileContext } from 'providers/profile'
import Tooltip from 'components/Tooltip'
import Button from 'components/Button'
import CalendarIcon from 'assets/icons/calendar-icon'
import { SiteContext } from 'providers/site'
import { getAssessmentParameterCheckFields } from 'utils/getAssessmentParameterCheckFields'
import PulsingNotification from 'features/PulsingNotification'
import { reassessmentTriggerReasons } from 'constants/reassessmentTriggerReasons'
import { defaultSiteProposalValidationResults } from 'constants/defaultSiteProposalValidationResults'
import ReassessIcon from 'assets/icons/reassess-icon'
import AssessIcon from 'assets/icons/assess-icon'
import {
  SpeciesConfirmDeclineListContainer,
  SpeciesConfirmDeclineListHalfSide,
  SpeciesConfirmDeclineListTitle,
  SpeciesConfirmDeclineListTitleIcon,
  SpeciesConfirmDeclineListTitleIconWrapper,
  SpeciesConfirmDeclineIcon,
  SpeciesLastReassessmentCalendarWrapper,
  SpeciesCalendarIconWrapper,
  SpeciesCalendarDivider,
  SpeciesCalendarDateText,
  SpeciesValidationIcon,
  SpeciesDeleteIconWrapper, SpeciesValidationIconWrapper, SpecieHeaderActionMenuWrapper
} from './style'
import { formatDate } from 'utils/dateFormat'

const formattedRejectIconTooltipHint = (item) => {
  return (item?.triggerReasonId || []).map((element) => {
    return reassessmentTriggerReasons.find((elementId) => {
      return elementId.id === element
    })?.name
  }).filter(Boolean)
}

const tooltipXOffset = 0
const tooltipYOffset = -16

const QualifyingStatusIcon = ({ item, nonQualified }) => {
  const overlay = nonQualified
    ? { label: 'nonQualifying', infoTooltip: formattedRejectIconTooltipHint(item).join(', ') }
    : { label: 'qualifies' }

  return (
    <SpeciesConfirmDeclineIcon type={nonQualified ? 'rejected' : 'confirmed'}>
      <Tooltip
        style={{ zIndex: 1000 }}
        placement='top'
        trigger={['hover']}
        align={{ offset: [tooltipXOffset, tooltipYOffset] }}
        overlay={overlay}
      >
        <div>
          {nonQualified ? <RejectIcon /> : <ConfirmIcon />}
        </div>
      </Tooltip>
    </SpeciesConfirmDeclineIcon>
  )
}

const ValidationCounter = ({ fieldsToCompleteNumber }) => {
  if (fieldsToCompleteNumber.length <= 0) {
    return null
  }

  return (
    <SpeciesValidationIconWrapper>
      <SpeciesValidationIcon>
        <PulsingNotification number={fieldsToCompleteNumber.length} />
      </SpeciesValidationIcon>
    </SpeciesValidationIconWrapper>
  )
}

const SpeciesActionButton = ({ siteStatusId, hasLastReassessmentDate, onClick }) => {
  const isDraft = siteStatusId === SITE_STATUS_DRAFT_ID
  let icon = <ViewSiteIcon />
  let messageId = 'view'

  if (isDraft && hasLastReassessmentDate) {
    icon = <ReassessIcon />
    messageId = 'reassess'
  } else if (isDraft) {
    icon = <AssessIcon />
    messageId = 'assess'
  }

  return (
    <Button height='32px' marginTop='0' onClick={onClick}>
      {icon}
      <FormattedMessage id={messageId} />
    </Button>
  )
}

const SpeciesConfirmDeclineList = ({
  item,
  permitDelete,
  lastItem,
  onClick,
  site,
  getSiteSpecies,
  openRemovalModal,
  nonQualified
}) => {
  const dateToShow = item.isReassessed ? formatDate(item.updatedAt) : formatDate(item.lastReassessmentDate)
  const params = useParams()
  const { profile } = useContext(ProfileContext)
  const { siteProposalFieldValidation, isEditable } = useContext(SiteContext)
  const speciesValidationMap = siteProposalFieldValidation?.species || {}

  const handleSpeciesAction = (isApproved) => {
    confirmSpecies(item.id, params.siteHistoryId, isApproved).then((data) => {
      getSiteSpecies()
    })
  }

  const removeSpecie = () => {
    openRemovalModal(item)
  }

  const actionMenuItems = item.isApprovedBySecretariat ? [{
    icon: <RejectIcon />,
    title: 'rejectSpecies',
    onClick: () => {
      handleSpeciesAction({ isApproved: false })
    },
    isDestructive: true
  }] : [{
    icon: <ConfirmIcon />,
    title: 'confirmSpecies',
    onClick: () => {
      handleSpeciesAction({ isApproved: true })
    }
  }]

  async function handleClick() {
    if (
      !item.isReassessed &&
      site.SiteStatusId === SITE_STATUS_DRAFT_ID &&
      !canNotViewUpdateSitesData(profile.RoleId) &&
      site.reassessmentFlag
    ) {
      try {
        const data = await requestUpdateSiteSpecie(
          item.id,
          params.siteHistoryId,
          item.historyId,
          { reassessDateStatus: false }
        );
        onClick(data);
        getSiteSpecies(); // Refresh the species list after reassessment
      } catch (error) {
        console.error("Error updating reassessment status:", error);
      }
      return;
    }
    onClick(item);
  }


  const specieId = item.reassessmentFlag ? item.historyId : item.id

  const speciesValidationForSpecie = speciesValidationMap[specieId]
  const validationSpecies = speciesValidationForSpecie || defaultSiteProposalValidationResults.species

  const parametersId = item.SpeciesAssesmentParameters.map((element) => {
    return element.id
  })

  let parametersFields = []
  if (speciesValidationForSpecie && parametersId.length > 0) {
    parametersFields = parametersId.flatMap((element) => {
      const parameter = item.SpeciesAssesmentParameters.find(p => p.id === element)
      const isIbaOnly = parameter?.iba_only === true
      const validationFields = getAssessmentParameterCheckFields(siteProposalFieldValidation, element)

      if (!validationFields || !siteProposalFieldValidation?.updated) {
        return []
      }

      // When iba_only is true, exclude bestGlobal, sourceGlobalData, and natureOfOccurence from validation count
      if (isIbaOnly) {
        const filteredFields = { ...validationFields }
        // Remove these fields from validation count when iba_only is true
        delete filteredFields.bestGlobal
        delete filteredFields.sourceGlobalData
        delete filteredFields.natureOfOccurence
        return Object.values(filteredFields)
      }

      return Object.values(validationFields)
    })
  }

  let fieldsToCompleteNumber = []
  if (!validationSpecies.triggerReasonId) {
    fieldsToCompleteNumber = [1]
  } else if (!nonQualified) {
    fieldsToCompleteNumber = Object.values(validationSpecies).filter((element) => {
      return element === false
    }).concat(parametersFields.filter((value) => {
      return !value
    }))
  }

  const hasLastReassessmentDate = Boolean(item.lastReassessmentDate)
  const isNominatedAndConfirmable = (
    site.SiteStatusId === SITE_STATUS_NOMINATED_ID &&
    canConfirmSites(profile.RoleId)
  )
  const canDeleteInDateFlow = permitDelete && isEditable
  const canDeleteInNoDateFlow = permitDelete && isEditable && site.SiteStatusId === SITE_STATUS_DRAFT_ID
  const showStatusIconInNoDateFlow = site.reassessmentFlag

  const renderDateInfo = () => {
    if (!hasLastReassessmentDate) {
      return null
    }

    return (
      <Tooltip
        style={{ zIndex: 1000 }}
        placement='top'
        trigger={['hover']}
        align={{ offset: [tooltipXOffset, tooltipYOffset] }}
        overlay={{ label: 'lastTimeReassessed' }}
      >
        <SpeciesLastReassessmentCalendarWrapper>
          <SpeciesCalendarIconWrapper>
            <CalendarIcon />
          </SpeciesCalendarIconWrapper>
          <SpeciesCalendarDateText>
            {dateToShow}
          </SpeciesCalendarDateText>
          <SpeciesCalendarDivider />
        </SpeciesLastReassessmentCalendarWrapper>
      </Tooltip>
    )
  }

  const renderActionMenu = () => {
    if (!isNominatedAndConfirmable) {
      return null
    }

    return (
      <SpecieHeaderActionMenuWrapper>
        <ActionsMenu table={false} dropdownItems={actionMenuItems} margin='0' />
      </SpecieHeaderActionMenuWrapper>
    )
  }

  const renderDeleteButton = () => {
    const canDelete = hasLastReassessmentDate ? canDeleteInDateFlow : canDeleteInNoDateFlow

    if (!canDelete) {
      return null
    }

    return (
      <SpeciesDeleteIconWrapper onClick={removeSpecie}>
        <RemoveIcon />
      </SpeciesDeleteIconWrapper>
    )
  }

  const renderStatusIcon = () => {
    if (!hasLastReassessmentDate && !showStatusIconInNoDateFlow) {
      return null
    }

    return <QualifyingStatusIcon item={item} nonQualified={nonQualified} />
  }


  return (
    <SpeciesConfirmDeclineListContainer lastItem={lastItem} onClick={() => {}}>
      <SpeciesConfirmDeclineListHalfSide>
        <SpeciesConfirmDeclineListTitleIconWrapper>
          <SpeciesConfirmDeclineListTitleIcon isRejected={nonQualified} />
        </SpeciesConfirmDeclineListTitleIconWrapper>
        <SpeciesConfirmDeclineListTitle isRejected={nonQualified}>
          {item.scientificName}
        </SpeciesConfirmDeclineListTitle>
      </SpeciesConfirmDeclineListHalfSide>
      <SpeciesConfirmDeclineListHalfSide>
        {renderDateInfo()}
        {renderStatusIcon()}
        <SpeciesActionButton
          siteStatusId={site.SiteStatusId}
          hasLastReassessmentDate={hasLastReassessmentDate}
          onClick={handleClick}
        />
        {renderDeleteButton()}
        {renderActionMenu()}
        <ValidationCounter fieldsToCompleteNumber={fieldsToCompleteNumber} />
      </SpeciesConfirmDeclineListHalfSide>
    </SpeciesConfirmDeclineListContainer>
  )
}

export default SpeciesConfirmDeclineList

SpeciesConfirmDeclineList.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    historyId: PropTypes.string,
    scientificName: PropTypes.string.isRequired,
    isReassessed: PropTypes.bool,
    updatedAt: PropTypes.string,
    lastReassessmentDate: PropTypes.string,
    isApprovedBySecretariat: PropTypes.bool,
    reassessmentFlag: PropTypes.bool,
    triggerReasonId: PropTypes.arrayOf(PropTypes.number),
    SpeciesAssesmentParameters: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        iba_only: PropTypes.bool
      })
    )
  }).isRequired,
  permitDelete: PropTypes.bool,
  lastItem: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  site: PropTypes.shape({
    SiteStatusId: PropTypes.number,
    reassessmentFlag: PropTypes.bool
  }).isRequired,
  getSiteSpecies: PropTypes.func.isRequired,
  openRemovalModal: PropTypes.func.isRequired,
  nonQualified: PropTypes.bool
}

SpeciesConfirmDeclineList.defaultProps = {
  permitDelete: false,
  lastItem: false,
  nonQualified: false
}

QualifyingStatusIcon.propTypes = {
  item: SpeciesConfirmDeclineList.propTypes.item,
  nonQualified: PropTypes.bool
}

QualifyingStatusIcon.defaultProps = {
  nonQualified: false
}

ValidationCounter.propTypes = {
  fieldsToCompleteNumber: PropTypes.arrayOf(PropTypes.any).isRequired
}

SpeciesActionButton.propTypes = {
  siteStatusId: PropTypes.number,
  hasLastReassessmentDate: PropTypes.bool,
  onClick: PropTypes.func.isRequired
}

SpeciesActionButton.defaultProps = {
  siteStatusId: null,
  hasLastReassessmentDate: false
}
